import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { searchNodeDefinitions } from 'api/node_definitions/endpoints/searchNodeDefinitions';
import type { NodeDefinitionRow } from 'api/node_definitions/endpoints/getNodeDefinitions';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';

interface Props {
  // The stored `zone_name|name` node-definition path, or null for "no node definition".
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  /** Floating label. Omit to render a bare control (the caller supplies its own label), as `EditString` does. */
  readonly label?: string;
  /** Text shown while nothing is selected. Say what empty means, rather than restating the label. */
  readonly placeholder?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Restrict the searchable set to one zone (server-side `zone_name` equals). Undefined widens to
  // all authorized node definitions.
  readonly zoneName?: string;
  // Restrict to one node type (server-side equals). Undefined widens to every type. Not a union:
  // node types are server-driven (`GET /node_types`).
  readonly type?: string;
}

const getKey = (n: NodeDefinitionRow) => `${n.zone_name}|${n.name}`;
const getLabel = (n: NodeDefinitionRow): string => `${n.name} (${n.zone_name})`;

const splitPath = (path: string): { readonly zone_name: string; readonly name: string } => {
  const [zone_name = '', name = ''] = path.split('|');
  return { zone_name, name };
};

// A key holds both display parts, so an item can be reconstructed from it alone. `node_definition_id`
// is not part of the key and this control never reads it back, hence the placeholder 0.
const asItem = (path: string): NodeDefinitionRow => ({ node_definition_id: 0, ...splitPath(path) });

/**
 * Searchable dropdown for node definitions keyed on the `zone_name|name` path, for callers that store the composite path —
 * the shape a node's `config` carries, and the natural key a transport round-trips. Selection returns the path.
 *
 * Sibling of the id-keyed {@link EditNodeDefinitionReference}, whose props are kept in step with these: only `value` /
 * `onChange` differ, in type *and* in meaning, which is the whole reason there are two components rather than one with a
 * `string | number` value.
 *
 * Backed by server-side search, so the option list is not bounded by what fits in one eager fetch. Two-column row layout
 * (name + zone, muted right-aligned as informational context); the server matches on name only, zone is shown so users can
 * disambiguate visually.
 */
export const EditNodeDefinitionPathReference = ({ value, onChange, label, placeholder, mandatory, disabled, zoneName, type }: Props) => {
  const selected = useMemo<NodeDefinitionRow | null>(() => (value === null || value === '' ? null : asItem(value)), [value]);

  // Re-created when the scope changes so the model re-fetches; stable otherwise (a fresh closure
  // would rebuild the model every render).
  const source = useMemo<ReferenceSource<NodeDefinitionRow>>(
    () => ({
      kind: 'search',
      search: (query, limit) => searchNodeDefinitions({ search: query || undefined, limit, zone_name: zoneName, type }),
      // Required by the source contract, which cannot know whether a consumer ever holds a key-only
      // value — but neither runs here: this key carries the whole display value, so the item the
      // control is handed is already labelled and there is nothing to hydrate. Built from the key
      // anyway, since that is both the cheapest correct answer and the only one available offline.
      resolve: async (key) => asItem(String(key)),
      interimValue: (key) => asItem(String(key)),
    }),
    [zoneName, type]
  );

  return (
    <EditReference<NodeDefinitionRow>
      label={label}
      placeholder={placeholder}
      value={selected}
      onChange={(n) => onChange(n !== null ? getKey(n) : null)}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(n, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{n.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{n.zone_name}</span>
        </div>
      )}
    />
  );
};
